import { useCallback, useEffect } from "react";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha
} from "react-google-recaptcha-v3";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps.ts";
import useSettingsPageProps from "@hooks/useSettingsPageProps";

import { recaptchaVerifyApi } from "@api/checkoutApi";

interface PropTypes {
    isCaptchaVerified: boolean;
    onCaptchaVerified: (s: boolean) => void;
}

const ReCaptcha = ({ isCaptchaVerified, onCaptchaVerified }: PropTypes) => {
    const { recaptchaSiteKey } = useSettingsPageProps();

    return (
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
            <ReCaptchaChallenge
                isCaptchaVerified={isCaptchaVerified}
                onCaptchaVerified={onCaptchaVerified}
            />
        </GoogleReCaptchaProvider>
    );
};

const ReCaptchaChallenge = ({
    isCaptchaVerified,
    onCaptchaVerified
}: PropTypes) => {
    const { parentUser } = useCheckoutPageProps();
    const isParentUserLoggedIn = !!parentUser;
    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleReCaptchaVerify = useCallback(async () => {
        // logged-in users do not need to verify reCAPTCHA
        if (isParentUserLoggedIn) {
            onCaptchaVerified(true);
            return;
        }
        if (!executeRecaptcha) {
            return;
        }
        try {
            const token = await executeRecaptcha("checkout");

            recaptchaVerifyApi({ token }).then(response => {
                // score is 0.0 to 1.0 and represents the probability that the user is a human.
                // minimum can be increased once we have a design to prove that the user is a human.
                if (response.data.success && response.data.score >= 0.2) {
                    onCaptchaVerified(true);
                    console.log("INFO: reCaptcha is verified");
                } else {
                    onCaptchaVerified(false);
                    console.log("INFO: reCaptcha is not verified");
                }
            });
        } catch (error) {
            if (
                error instanceof TypeError &&
                error.message.includes("undefined")
            ) {
                console.log("INFO: reCAPTCHA is loading.");
                // setTimeout(() => handleReCaptchaVerify(), 100);
            } else {
                console.error("Error verifying reCAPTCHA:", error);
            }
        }
    }, [executeRecaptcha]);

    useEffect(() => {
        const verifyDebounce = setTimeout(() => {
            if (!isCaptchaVerified) {
                handleReCaptchaVerify().then(r => r);
            }
        }, 2000);
        return () => clearTimeout(verifyDebounce);
    }, [isCaptchaVerified, handleReCaptchaVerify]);
    // Will return a challenge if the request is not verified, once design is ready.
    return null;
};

export default ReCaptcha;
